// SampleBulkAdoptPage — the received shelf as ONE worksheet (admin shell only).
//
// The per-sample adopt card (SampleAdoptCard) is the right shape for a single
// arrival and the wrong shape for a backlog: 36 samples meant 36 pages, so the
// backlog never moved. Here every received, not-yet-adopted sample is one row
// the server pre-filled — suggested parent product, axis, the colours the
// sample was briefed in, codes, supplier, cost — and the operator scans, fixes
// and adopts the ticked rows in one pass. Photos are deliberately NOT carried
// (the reference images belong to other sellers' listings); rows say so with a
// "no photo" chip and the operator uploads their own from the product page.

import { useMemo, useState } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeft, BookPlus, ExternalLink, Loader2 } from 'lucide-react'
import { apiUrl } from '../../lib/api'
import { toast } from '../../lib/toast'
import { bulkAdopt, getBulkAdoptPlan, type BulkAdoptResult, type BulkAdoptRow } from './api'
import { useT } from '../../i18n'
import { sampleBulkAdoptI18n } from '../../i18n/samples/bulk-adopt'

type EcosystemOption = { id: string; name: string }

async function fetchEcosystems(): Promise<EcosystemOption[]> {
  const res = await fetch(apiUrl('/fulfillment-ecosystems'))
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const j = (await res.json()) as { items?: Array<Record<string, unknown>> }
  return (j.items ?? [])
    .filter((r) => r.active !== false)
    .map((r) => ({ id: String(r.id), name: String(r.display_name ?? r.key ?? r.id) }))
}

// Every leather product in the catalog lives in the NJ warehouse ecosystem, so
// that is the batch default; the operator can still switch it for the batch.
const DEFAULT_ECOSYSTEM_HINT = 'usa warehouse'

type RowState = {
  include: boolean
  mode: 'add_variation' | 'create_product'
  targetId: string | null
  familyCode: string
  title: string
  axisName: string
  costUsd: string
  values: Array<{ value: string; sku_code: string; selected: boolean }>
}

function seedState(row: BulkAdoptRow): RowState {
  return {
    include: true,
    mode: row.mode,
    targetId: row.target_master_catalog_id,
    familyCode: row.family_code,
    title: row.title,
    axisName: row.axis_name,
    costUsd: row.cost_usd ?? '',
    values: row.values.map((v) => ({ ...v })),
  }
}

const inputCls =
  'w-full px-2 py-1 text-xs border border-slate-300 appearance-dark:border-[var(--color-border)] rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50'
const labelCls = 'block text-[11px] font-medium uppercase tracking-wide text-slate-500 appearance-dark:text-[var(--color-text-muted)]'

export function SampleBulkAdoptPage() {
  // The samples list hands over exactly the rows the operator ticked; opening
  // the page without a selection still plans the whole received shelf.
  const t = useT(sampleBulkAdoptI18n)
  const search = useSearch({ strict: false }) as { ids?: string }
  const ids = search?.ids
  const planQ = useQuery({
    queryKey: ['sample-bulk-adopt-plan', ids ?? 'all'],
    queryFn: () => getBulkAdoptPlan(ids),
  })
  const ecosystemsQ = useQuery({ queryKey: ['fulfillment-ecosystems-options'], queryFn: fetchEcosystems })

  const [state, setState] = useState<Record<string, RowState>>({})
  const [ecosystemId, setEcosystemId] = useState('')
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<BulkAdoptResult | null>(null)

  const rows = useMemo(() => planQ.data?.rows ?? [], [planQ.data])

  // Seed the editable worksheet from the server's proposal the first time the
  // plan lands (adjust-state-during-render, same as the single adopt card):
  // an effect would repaint the form after the operator already saw it.
  const [seeded, setSeeded] = useState(false)
  if (!seeded && rows.length) {
    setSeeded(true)
    setState(Object.fromEntries(rows.map((row) => [row.sample_request_id, seedState(row)])))
  }
  const ecosystemOptions = ecosystemsQ.data ?? []
  if (!ecosystemId && ecosystemOptions.length) {
    const preferred = ecosystemOptions.find((o) =>
      o.name.toLowerCase().includes(DEFAULT_ECOSYSTEM_HINT),
    )
    setEcosystemId((preferred ?? ecosystemOptions[0]).id)
  }

  const patch = (id: string, changes: Partial<RowState>) =>
    setState((prev) => ({ ...prev, [id]: { ...prev[id], ...changes } }))

  const selectedCount = rows.filter((r) => state[r.sample_request_id]?.include).length

  async function submit() {
    const payloadRows = rows
      .map((row) => ({ row, s: state[row.sample_request_id] }))
      .filter(({ s }) => s?.include)
      .map(({ row, s }) => ({
        sample_request_id: row.sample_request_id,
        mode: s.mode,
        axis_name: s.axisName,
        values: s.values.filter((v) => v.selected).map((v) => ({ value: v.value, sku_code: v.sku_code })),
        master_catalog_id: s.mode === 'add_variation' ? s.targetId : null,
        family_code: s.mode === 'create_product' ? s.familyCode : null,
        title: s.mode === 'create_product' ? s.title : null,
        category: s.mode === 'create_product' ? row.category : null,
        supplier_id: s.mode === 'create_product' ? row.supplier_id : null,
        material: s.mode === 'create_product' ? row.material : null,
        source_url: s.mode === 'create_product' ? row.source_url : null,
        cost_usd: s.costUsd.trim() ? s.costUsd.trim() : null,
      }))
    const empty = payloadRows.find((r) => r.values.length === 0)
    if (empty) {
      toast.error(t.needsValue)
      return
    }
    if (!payloadRows.length || !ecosystemId) return
    setBusy(true)
    try {
      const result = await bulkAdopt({ fulfillment_ecosystem_id: ecosystemId, rows: payloadRows })
      setOutcome(result)
      toast.success(t.addedToCatalog(result.adopted))
      setSeeded(false)
      await planQ.refetch()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t.bulkAdoptFailed)
    } finally {
      setBusy(false)
    }
  }

  if (planQ.isLoading) {
    return (
      <div className="p-6 text-sm text-slate-500 appearance-dark:text-[var(--color-text-muted)] inline-flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> {t.loadingWorksheet}
      </div>
    )
  }
  if (planQ.error) {
    return <div className="p-6 text-sm text-rose-600 appearance-dark:text-[var(--color-danger)]">{t.loadError}</div>
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/admin/samples" className="text-xs text-slate-500 appearance-dark:text-[var(--color-text-muted)] hover:text-slate-700 appearance-dark:hover:text-[var(--color-text)] inline-flex items-center gap-1">
            <ArrowLeft size={12} /> {t.samples}
          </Link>
          <h1 className="text-lg font-semibold text-slate-900 appearance-dark:text-[var(--color-text)]">{t.title}</h1>
          <p className="text-xs text-slate-500 appearance-dark:text-[var(--color-text-muted)]">
            {ids ? t.selectedCount(rows.length) : t.waitingCount(rows.length)}{t.photosNote}
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className={labelCls} htmlFor="bulk-adopt-ecosystem">{t.ecosystem}</label>
            <select
              id="bulk-adopt-ecosystem"
              className={inputCls}
              value={ecosystemId}
              onChange={(e) => setEcosystemId(e.target.value)}
            >
              {ecosystemOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
          <button
            className="px-3 py-2 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1"
            disabled={busy || !selectedCount || !ecosystemId}
            onClick={submit}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <BookPlus size={14} />}
            {t.adoptSelected(selectedCount)}
          </button>
        </div>
      </div>

      {outcome && (
        <div className="rounded border border-slate-200 appearance-dark:border-[var(--color-border)] bg-slate-50 appearance-dark:bg-[var(--color-surface-muted)] p-3 text-xs text-slate-700 appearance-dark:text-[var(--color-text)] space-y-1">
          <div className="font-semibold">
            {t.outcome(outcome.adopted, outcome.failed)}
          </div>
          {outcome.results
            .filter((r) => r.status !== 'adopted')
            .map((r) => (
              <div key={r.sample_request_id} className="text-rose-700 appearance-dark:text-[var(--color-danger)]">
                {rows.find((row) => row.sample_request_id === r.sample_request_id)?.title ?? r.sample_request_id}: {r.message}
              </div>
            ))}
        </div>
      )}

      {!rows.length && (
        <div className="rounded border border-slate-200 appearance-dark:border-[var(--color-border)] p-6 text-sm text-slate-500 appearance-dark:text-[var(--color-text-muted)]">
          {t.emptyShelf}
        </div>
      )}

      <div className="space-y-3">
        {rows.map((row) => {
          const s = state[row.sample_request_id]
          if (!s) return null
          const candidate = row.candidates.find((c) => c.master_catalog_id === s.targetId) ?? null
          return (
            <div
              key={row.sample_request_id}
              className={`rounded border p-3 ${s.include ? 'border-slate-300 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)]' : 'border-slate-200 appearance-dark:border-[var(--color-border)] bg-slate-50 appearance-dark:bg-[var(--color-surface-muted)] opacity-60'}`}
            >
              <div className="flex flex-wrap items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={s.include}
                  onChange={(e) => patch(row.sample_request_id, { include: e.target.checked })}
                  aria-label={t.include(row.title)}
                />
                <div className="min-w-[220px] flex-1">
                  <div className="text-sm font-semibold text-slate-900 appearance-dark:text-[var(--color-text)]">
                    #{row.request_number} · {row.title}
                  </div>
                  <div className="text-[11px] text-slate-500 appearance-dark:text-[var(--color-text-muted)] space-x-2">
                    <span>{row.producer_name ?? t.noFactory}</span>
                    {row.quote_raw && <span>{t.quote(row.quote_raw)}</span>}
                    {row.material && <span>· {row.material}</span>}
                    {row.market_price && <span>{t.listing(row.market_price)}</span>}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {row.warnings.map((w) => (
                      <span
                        key={w}
                        className="inline-flex items-center gap-1 rounded bg-amber-50 appearance-dark:bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[10px] text-amber-800 appearance-dark:text-[var(--color-warning)] border border-amber-200 appearance-dark:border-[var(--color-border)]"
                      >
                        <AlertTriangle size={10} /> {(t.warnings as Record<string, string>)[w] ?? w}
                      </span>
                    ))}
                    {row.source_url && (
                      <a
                        href={row.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 rounded bg-slate-50 appearance-dark:bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[10px] text-slate-600 appearance-dark:text-[var(--color-text-muted)] border border-slate-200 appearance-dark:border-[var(--color-border)]"
                      >
                        <ExternalLink size={10} /> {t.sourceListing}
                      </a>
                    )}
                  </div>
                </div>

                <div className="min-w-[260px] flex-1 space-y-2">
                  <div className="flex gap-1">
                    <button
                      className={`px-2 py-1 text-[11px] rounded border ${s.mode === 'add_variation' ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-300 appearance-dark:border-[var(--color-border)] text-slate-600 appearance-dark:text-[var(--color-text-muted)]'} disabled:opacity-40`}
                      disabled={!row.candidates.length}
                      onClick={() => patch(row.sample_request_id, { mode: 'add_variation' })}
                    >
                      {t.variationOfExisting}
                    </button>
                    <button
                      className={`px-2 py-1 text-[11px] rounded border ${s.mode === 'create_product' ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-300 appearance-dark:border-[var(--color-border)] text-slate-600 appearance-dark:text-[var(--color-text-muted)]'}`}
                      onClick={() => patch(row.sample_request_id, { mode: 'create_product' })}
                    >
                      {t.newProduct}
                    </button>
                  </div>

                  {s.mode === 'add_variation' ? (
                    <div className="space-y-1">
                      <label className={labelCls} htmlFor={`target-${row.sample_request_id}`}>{t.parentProduct}</label>
                      <select
                        id={`target-${row.sample_request_id}`}
                        className={inputCls}
                        value={s.targetId ?? ''}
                        onChange={(e) => {
                          const next = row.candidates.find((c) => c.master_catalog_id === e.target.value)
                          patch(row.sample_request_id, {
                            targetId: e.target.value,
                            axisName: next?.axis_names[0] ?? s.axisName,
                          })
                        }}
                      >
                        {row.candidates.map((c) => (
                          <option key={c.master_catalog_id} value={c.master_catalog_id}>
                            {c.platform_sku} — {c.title} ({c.sku_count} SKU)
                          </option>
                        ))}
                      </select>
                      {candidate && !candidate.variant_addable && (
                        <div className="text-[11px] text-amber-700 appearance-dark:text-[var(--color-warning)]">
                          {t.severalParents}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className={labelCls} htmlFor={`family-${row.sample_request_id}`}>{t.productCode}</label>
                        <input
                          id={`family-${row.sample_request_id}`}
                          className={inputCls}
                          value={s.familyCode}
                          onChange={(e) => patch(row.sample_request_id, { familyCode: e.target.value.toUpperCase() })}
                        />
                      </div>
                      <div>
                        <label className={labelCls} htmlFor={`title-${row.sample_request_id}`}>{t.productTitle}</label>
                        <input
                          id={`title-${row.sample_request_id}`}
                          className={inputCls}
                          value={s.title}
                          onChange={(e) => patch(row.sample_request_id, { title: e.target.value })}
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={labelCls} htmlFor={`axis-${row.sample_request_id}`}>{t.axis}</label>
                      <input
                        id={`axis-${row.sample_request_id}`}
                        className={inputCls}
                        value={s.axisName}
                        onChange={(e) => patch(row.sample_request_id, { axisName: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor={`cost-${row.sample_request_id}`}>{t.costUsd}</label>
                      <input
                        id={`cost-${row.sample_request_id}`}
                        className={inputCls}
                        value={s.costUsd}
                        placeholder="—"
                        onChange={(e) => patch(row.sample_request_id, { costUsd: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="min-w-[240px] flex-1">
                  <div className={labelCls}>{t.values(s.values.filter((v) => v.selected).length)}</div>
                  <div className="mt-1 max-h-40 space-y-1 overflow-auto pr-1">
                    {s.values.map((v, index) => (
                      <div key={v.value} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={v.selected}
                          aria-label={`${row.title} ${v.value}`}
                          onChange={(e) => {
                            const values = s.values.map((item, i) =>
                              i === index ? { ...item, selected: e.target.checked } : item,
                            )
                            patch(row.sample_request_id, { values })
                          }}
                        />
                        <span className="w-24 shrink-0 truncate text-[11px] text-slate-700 appearance-dark:text-[var(--color-text)]">{v.value}</span>
                        <input
                          className={inputCls}
                          value={v.sku_code}
                          aria-label={t.skuCodeFor(v.value)}
                          onChange={(e) => {
                            const values = s.values.map((item, i) =>
                              i === index ? { ...item, sku_code: e.target.value.toUpperCase() } : item,
                            )
                            patch(row.sample_request_id, { values })
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
